import axios from 'axios';
import * as Sentry from '@sentry/vue';
import type { AxiosInstance, AxiosResponse, AxiosPromise } from 'axios';
import type {
	API_GetSportelloEntiResponse,
	API_GetSportelloServiziResponse,
	API_GetUserResponse,
	API_GetListTipiSegnalazioneResponse,
	API_GetSegnalazioneResponse,
	API_GetPosizioniListResponse,
	API_GetDichiarazioniListResponse,
	API_GetSegnalazioniListResponse,
	API_UploadFileResponse,
	API_SendFormRateizzazioneResponse,
	API_TombePickList,
	API_RiesamePickList,
	API_SendFormProrogaResponse,
	API_GetEntiPerCategoriaResponse,
	API_GetTipoStrutturaOptionsResponse,
} from '@/types/api';
import type {
	List_PosizioniObject,
	List_TipiSegnalazioneObject,
	List_DichiarazioniObject,
	List_SegnalazioniObject,
	List_TombePickListObject,
	List_RiesamePickListObject,
	List_EntiPerCategoriaObject,
	List_DocumentiDaVerificare,
	List_Documenti,
	EnteGES,
	ServizoPerEntePagamento,
	EntiObject,
} from '@/types/list';

import type { NuovaDichiarazioneMezziPermanenti } from '@/types/formMezzoPermanente';
import {
	TipiMezzi,
	type DichiarazioneMezziPermanenti1,
	type MezzoPermanente,
	type FileMP,
} from '@/types/mezzoPermanente';

import type { User_UserObject } from '@/types/user';
import type { Segnalazione_SegnalazioneObject } from '@/types/segnalazione';
import type { FormProrogaType, FormRateizzazioneType } from '@/types/formRateizzazione';
import type { DocumentoAllegato, WSPosizioneDocumentoData } from '@/types/formRateizzazione';
import type { NuovoAllacciamento_TombaObject } from '@/types/tomba';
import type { RiesameObject } from '@/types/riesame';
import type { AffissioneManifestiObject } from '@/types/affissioneManifesti';
import type { IstanzaContribuenteObject } from '@/types/istanzaContribuente';
import type { DelegaImpostaSoggiornoObject } from '@/types/delegaImpostaSoggiorno';
import type { AbilitazioneStrutturaRicettivaObject } from '@/types/abilitazioneStrutturaRicettiva';
import type { ParcheggioEnte } from '@/types/abbonamentoParcheggi';
import type { LocandineObject } from '@/types/locandine';
import type { StragiudizialeObject } from '@/types/stragiudiziale';

import ErrorHandler from '@/classes/ErrorHandler';
import type { mooneyForm, pagamentoPagoPa, pagamentoPagoPaNonPredeterminato } from '@/types/pagamento';
class API {
	private baseURL: string;

	constructor(baseURL: string) {
		this.baseURL = baseURL;
	}

	private axiosInstance: AxiosInstance | null = null;

	private client(): AxiosInstance {
		if (this.axiosInstance !== null) {
			return this.axiosInstance;
		}

		this.axiosInstance = axios.create({
			baseURL: this.baseURL,
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			withCredentials: true,
		});

		this.axiosInstance.defaults.withCredentials = true;
		this.axiosInstance.defaults.withXSRFToken = true;

		// Interceptor per le richieste - modifica il body prima dell'invio
		this.axiosInstance.interceptors.request.use(
			async (config) => {
				// qui possiamo modificare il body della richiesta
				// esempio: config.data = { ...config.data, timestamp: Date.now() }

				// aggiungi il token di google per verificare chiamate post
				// solo se non sono in sviluppo
				if (config.method == 'post') {

					try {
						let gtoken: string = '';

						// Ensure grecaptcha is defined before using it
						if (typeof grecaptcha !== 'undefined') {
							gtoken = await new Promise((resolve, reject) => {
								grecaptcha.ready(() => {
									const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

									// Usiamo un'action più semplice e valida
									const action = 'submit_form';

									grecaptcha.execute(recaptchaSiteKey, { action }).then(resolve).catch(reject);
								});
							});
						}

						if (config.data instanceof FormData) {
							config.data.append('g-recaptcha-response', gtoken);
						} else {
							config.data['g-recaptcha-response'] = gtoken;
						}
					} catch (error) {
						// console.debug('recaptcha not started', error);
						ErrorHandler.addLog('recaptcha not started', error);
					}
				}

				// server per aggiungere il body della richiesta su sentry
				try {
					Sentry.addBreadcrumb({
						category: 'body',
						data: config,
					});
				} catch (error) {
					ErrorHandler.addLog('Error adding request breadcrumb', error);
				}

				return config;
			},
			(error) => Promise.reject(error)
		);

		// Interceptor per le risposte
		// Success: pass through as-is.
		// Error: add a breadcrumb for tracing and re-reject — the actual Sentry capture
		// happens inside the individual catch blocks, where we have full call-site context.
		this.axiosInstance.interceptors.response.use(
			(response) => response,
			(error) => {
				ErrorHandler.addLog(
					`API error ${error?.response?.status ?? 'network'}: ${error?.config?.method?.toUpperCase() ?? ''} ${error?.config?.url ?? ''}`,
					{
						status: error?.response?.status ?? null,
						url: error?.config?.url ?? null,
						method: error?.config?.method ?? null,
						response_body: error?.response?.data ?? null,
					}
				);
				return Promise.reject(error);
			}
		);

		return this.axiosInstance;
	}

	// ritorna elenco dei comuni (enti)
	async getSportelloEnti(): Promise<EntiObject[]> {
		try {
			const response: AxiosResponse<API_GetSportelloEntiResponse> = await this.client().get(
				`/api/v1/generic/sportello/enti/list`
			);
			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
	}

	// ritorna elenco dei servizi per un determinato comune
	async getSportelloServizi(ente: string): AxiosPromise<API_GetSportelloServiziResponse> {
		return this.client().get(`/api/v1/generic/sportello/servizi/list?comune=${ente}`);
	}

	// ritorna elenco dei comuni (enti) in base alla categoria
	async getEntiPerCategoria(
		form: string,
		comune: string = ''
	): Promise<List_EntiPerCategoriaObject[]> {
		try {
			const response: AxiosResponse<API_GetEntiPerCategoriaResponse> = await this.client().get(
				`/api/v1/generic/ente/list/${form}/${comune}`
			);
			// //console.log(response.data);

			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
	}

	// ritorna elenco parcheggi con enti associati
	async getElencoParcheggi(): Promise<ParcheggioEnte[]> {
		try {
			const response: AxiosResponse<ParcheggioEnte[]> = await this.client().get(
				'/api/v1/parcheggi/elenco-parcheggi'
			);
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
	}

	// Utente
	async getUser(): Promise<User_UserObject | null> {
		try {
			const response: AxiosResponse<API_GetUserResponse> = await this.client().get('/api/v1/user');
			return response.data.data;
		} catch (error: any) {
			if (error.response?.status === 401) {
				// se l'utente non è autorizzato, non faccio nulla e non invio a Sentry nessun dato
				ErrorHandler.addLog('Utente non autorizzato durante getUser', error.response);
				return null;
			}
			ErrorHandler.handleError(error);
			return null;
		}
	}

	async editUser(data: any): Promise<any> {
		try {
			const response: AxiosResponse<API_GetUserResponse> = await this.client().post(
				'/api/v1/user/modifica_anagrafica',
				data
			);

			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	// Verifica cookie CSRF
	async getVerificaCookie(): Promise<void> {
		await this.client().get('sanctum/csrf-cookie');
	}

	// Aggiorna email
	async postAggiornaEmail(email: string, telefono: string): Promise<void> {
		const updateEmail = {
			Email_Comunicazione__c: email,
			Telefono_Comunicazione__c: telefono,
		};
		await this.client().post('/api/v1/user', updateEmail);
	}

	// Ritorna elenco tipi segnalazione
	async getListTipiSegnalazione(): Promise<List_TipiSegnalazioneObject[]> {
		try {
			const response: AxiosResponse<API_GetListTipiSegnalazioneResponse> = await this.client().get(
				'/api/v1/generic/segnalazione/options'
			);
			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
	}

	// Ritorna elenco enti
	async getListEnti(): Promise<any> {
		try {
			const response: AxiosResponse<API_GetListTipiSegnalazioneResponse> = await this.client().get(
				'/api/v1/generic/ente'
			);
			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
	}

	// Ritorna opzioni tipo struttura
	async getTipoStrutturaOptions(): Promise<string[]> {
		try {
			const response: AxiosResponse<API_GetTipoStrutturaOptionsResponse> = await this.client().get(
				'/api/v1/imposta-soggiorno/tipo-struttura/options'
			);
			return response.data.data.map((item: { valore: string }) => item.valore);
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
	}

	// Invia OTP
	async sendOTP(email: string): Promise<boolean> {
		try {
			const response = await this.client().post('/api/v1/generic/segnalazione/otp', { email });
			if (response.statusText !== 'Created') {
				throw new Error('Failed to send OTP');
			}
			return true;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	// Verifica OTP
	async verifyOTP(email: string, otp: string): Promise<boolean> {

		try {
			const body = {
				email: email,
				otp: otp,
			};

			const response = await this.client().post('/api/v1/generic/segnalazione/otp', body);
			if (response.data !== 'Otp Valido') {
				ErrorHandler.captureBusinessError('OTP validation failed', { body: body, response: response.data });
				return false;
			}
			return true;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	// Invia segnalazione
	async sendSegnalazione(form: any): Promise<boolean> {
		try {
			// const formData = new FormData();
			// formData.append('Descrizione_Segnalazione__c', descrizione);
			// formData.append('Email__c', email);
			// formData.append('Motivazione_Segnalazione__c', motivazione);
			// if (utenteNonLoggato) {
			// 	formData.append('Utente_non_loggato__c', utenteNonLoggato);
			// }
			// formData.append('Tipo_Segnalazione__c', tipoSegnalazione);
			// formData.append('Servizio__c', servizio);
			// formData.append('Ente__c', ente);

			const response = await this.client().post('/api/v1/generic/segnalazione/new', form);
			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	// Ritorna segnalazione
	async getSegnalazione(
		email: string,
		id: string
	): Promise<Segnalazione_SegnalazioneObject | null> {
		try {
			const response: AxiosResponse<API_GetSegnalazioneResponse> = await this.client().get(
				`/api/v1/generic/segnalazione/detail?email=${email}&id=${id}`
			);
			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return null;
		}
	}

	// Aggiunge nuovo commento alla segnalazione
	async postSegnalazioneNuovoCommento(form: any): Promise<boolean> {
		try {
			const response = await this.client().post('/api/v1/generic/segnalazione/commento', form);
			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	// Aggiunge nuova posizione
	async postAggiungiPosizione(
		codice_utente: string,
		codice_controllo: string
	): Promise<boolean | List_DichiarazioniObject> {
		try {
			const response = await this.client().post('/api/v1/posizione/new', {
				codice_utente,
				codice_controllo,
			});
			return response.data;
		} catch (error: any) {

			if (error.response?.status === 429) {
				return error.response.status;
			}

			ErrorHandler.handleError(error);
			return false;
		}
	}

	// Data una rappresentanza e un ente, recupera i documenti associati

	async getDocumentiToRappresentanza(
		idRappresentanza: string,
		ente: string
	): Promise<boolean | List_Documenti> {
		try {
			// ente potrebbe avere caratteri speciali, faccio un base64
			const _ente = btoa(ente);

			const response = await this.client().get(
				`/api/v1/posizione/documents/list/${idRappresentanza}/${_ente}`
			);

			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	// Ritorna elenco posizioni
	async getPosizioniList(): Promise<List_PosizioniObject[]> {
		try {
			const response: AxiosResponse<API_GetPosizioniListResponse> = await this.client().get(
				'/api/v1/posizione/list'
			);
			// //console.log(response.data.data);
			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
	}

	// Ritorna elenco dichiarazioni
	async getDichiarazioniList(): Promise<List_DichiarazioniObject[]> {
		try {
			const response: AxiosResponse<API_GetDichiarazioniListResponse> = await this.client().get(
				'/api/v1/dichiarazione/list'
			);
			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
	}

	// Ritorna elenco segnalazioni
	async getSegnalazioniList(): Promise<List_SegnalazioniObject[]> {
		try {
			const response: AxiosResponse<API_GetSegnalazioniListResponse> = await this.client().get(
				'/api/v1/generic/segnalazione/list'
			);
			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
	}

	// Tombe
	async sendFormNuovoAllacciamento(
		form: NuovoAllacciamento_TombaObject,
		files: Record<string, DocumentoAllegato>
	): Promise<boolean> {
		// Devo avere un array di file per la rateizzazione
		// //console.log('files', files, files);
		if (files && Object.keys(files).length > 0) {
			// cycle all the files saved in the files object
			try {
				form.filesAllegati = [];

				for (const key of Object.keys(files)) {
					form.filesAllegati.push({
						Id: files[key].Id,
					});
				}
				// //console.log('form', form);
				// //console.log('await', form);
				const response = await this.client().post('/api/v1/tombe/new', form);
				return response.status === 200 || response.status === 201;
			} catch (error: any) {
				ErrorHandler.handleError(error);
				return false;
			}
		}

		throw new Error('No files to upload');
	}

	async sendFormDelegaImpostaSoggiorno(
		form: DelegaImpostaSoggiornoObject
	): Promise<boolean> {
		try {
			const response = await this.client().post('/api/v1/imposta-soggiorno/new', form);
			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	async sendFormAbilitazioneStruttura(
		form: AbilitazioneStrutturaRicettivaObject,
		files?: Record<string, DocumentoAllegato>
	): Promise<boolean> {
		try {
			if (files && Object.keys(files).length > 0) {
				form.filesAllegati = [];
				for (const key of Object.keys(files)) {
					form.filesAllegati.push({
						Id: files[key].Id,
					});
				}
			}
			const response = await this.client().post('/api/v1/abilitazione-struttura-ricettiva/new', form);
			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	async getTombePicklist(): Promise<List_TombePickListObject[]> {
		try {
			const response: AxiosResponse<API_TombePickList> = await this.client().get(
				'/api/v1/tombe/tipo/options'
			);
			// //console.log(response.data);
			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return error;
		}
	}

	// Riesame
	async sendFormRiesame(
		form: RiesameObject,
		files: Record<string, DocumentoAllegato>
	): Promise<boolean> {
		// Devo avere un array di file per la rateizzazione
		// //console.log('files', files, files);
		if (files && Object.keys(files).length > 0) {
			// cycle all the files saved in the files object
			try {
				form.filesAllegati = [];

				for (const key of Object.keys(files)) {
					form.filesAllegati.push({
						Id: files[key].Id,
					});
				}
				// //console.log('form', form);
				// //console.log('await', form);
				const response = await this.client().post('/api/v1/riesame/new', form);
				return response.status === 200 || response.status === 201;
			} catch (error: any) {
				ErrorHandler.handleError(error);
				return false;
			}
		}

		throw new Error('No files to upload');
	}

	async getRiesamePicklist(): Promise<List_RiesamePickListObject[]> {
		try {
			const response: AxiosResponse<API_RiesamePickList> = await this.client().get(
				'/api/v1/riesame/istanza/options'
			);
			// //console.log(response.data);
			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return error;
		}
	}

	//Affissione Manifesti
	async sendFormAffissioneManifesti(
		form: AffissioneManifestiObject,
		files: Record<string, DocumentoAllegato>
	): Promise<boolean> {
		// Devo avere un array di file per la rateizzazione
		// //console.log('files', files, files);
		// cycle all the files saved in the files object
		try {
			if (files && Object.keys(files).length > 0) {
				form.filesAllegati = [];

				for (const key of Object.keys(files)) {
					form.filesAllegati.push({
						Id: files[key].Id,
					});
				}
			}
			// //console.log('form', form);
			// //console.log('await', form);
			const response = await this.client().post('/api/v1/affissione/manifesto', form);
			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	//Abbonamento Parcheggi
	async sendFormAbbonamentoParcheggi(
		form: any,
		files: Record<string, DocumentoAllegato>
	): Promise<boolean> {
		try {
			if (files && Object.keys(files).length > 0) {
				form.filesAllegati = [];

				for (const key of Object.keys(files)) {
					form.filesAllegati.push({
						Id: files[key].Id,
					});
				}
			}
			const response = await this.client().post('/api/v1/parcheggi/abbonamento/new', form);
			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	//Locandine
	async sendFormLocandine(
		form: LocandineObject,
		files: Record<string, DocumentoAllegato>
	): Promise<boolean> {
		// Devo avere un array di file per la rateizzazione
		// //console.log('files', files, files);
		// cycle all the files saved in the files object
		try {
			if (files && Object.keys(files).length > 0) {
				form.filesAllegati = [];

				for (const key of Object.keys(files)) {
					form.filesAllegati.push({
						Id: files[key].Id,
					});
				}
			}
			// //console.log('form', form);
			// //console.log('await', form);
			const response = await this.client().post('/api/v1/locandine/new', form);
			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	//Stragiudiziale
	async sendFormStragiudiziale(
		form: StragiudizialeObject,
		files: Record<string, DocumentoAllegato>
	): Promise<boolean> {
		// Devo avere un array di file per la rateizzazione
		// //console.log('files', files, files);
		// cycle all the files saved in the files object
		try {
			if (files && Object.keys(files).length > 0) {
				form.filesAllegati = [];

				for (const key of Object.keys(files)) {
					form.filesAllegati.push({
						Id: files[key].Id,
					});
				}
			}
			// //console.log('form', form);
			// //console.log('await', form);
			const response = await this.client().post('/api/v1/stragiudiziale/new', form);
			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	//Riesame istanza contribuente tari
	async sendFormIstanzaContribuente(
		form: IstanzaContribuenteObject,
		files: Record<string, DocumentoAllegato>
	): Promise<boolean> {
		// Devo avere un array di file per la rateizzazione
		// //console.log('files', files, files);
		// cycle all the files saved in the files object
		try {
			if (files && Object.keys(files).length > 0) {
				form.filesAllegati = [];

				for (const key of Object.keys(files)) {
					form.filesAllegati.push({
						Id: files[key].Id,
					});
				}
			}
			// //console.log('form', form);
			// //console.log('await', form);
			const response = await this.client().post('/api/v1/istanza-tributi/new', form);
			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	/**
	 * Checks the document, position, and service in the API.
	 *
	 * @param {string} documento - The document to check.
	 * @param {string} servizio - The service to check.
	 * @return {AxiosResponse<WSPosizioneDocumentoData>} A promise that resolves with the response data.
	 */
	async checkDocumentoRateizzazione(
		documento: string,
		servizio: string
	): Promise<WSPosizioneDocumentoData> {
		const _response = await this.client().get(
			'/api/v1/rateizzazione/documento?documento=' +
			documento +
			'&servizio=' +
			servizio
		);

		return _response.data;
	}

	async checkDocumentoRiesame(
		documento: string,
		servizio: string
	): Promise<WSPosizioneDocumentoData> {
		const _response = await this.client().get(
			'/api/v1/riesame/documento?documento=' +
			documento +
			'&servizio=' +
			servizio
		);

		return _response.data;
	}

	/**
	 * Checks the document, position, and service in the API.
	 *
	 * @param {string} documento - The document to check.
	 * @param {string} servizio - The service to check.
	 * @return {AxiosResponse<WSPosizioneDocumentoData>} A promise that resolves with the response data.
	 */
	async checkDocumentoProroga(
		documento: string,
		servizio: string
	): Promise<WSPosizioneDocumentoData> {
		const _response = await this.client().get(
			'/api/v1/proroga/documento' +
			'/check?documento=' +
			documento +
			'&servizio=' +
			servizio
		);

		return _response.data;
	}

	async checkDocumentoIstanzaTributi(data: {
		idDocument: string;
		sistema__c: string;
	}): Promise<WSPosizioneDocumentoData> {
		const _response = await this.client().get(
			'/api/v1/istanza-tributi/check-documenti?idDocument=' +
			data.idDocument +
			'&sistema__c=' +
			data.sistema__c
		);

		return _response.data;
	}

	/**
	 * Uploads a document with associated note.
	 *
	 * @param {File} file - The file to be uploaded
	 * @param {string} note - Additional note for the document
	 * @return {Promise<API_UploadFileResponse>} The response containing upload information
	 */
	async uploadDocumento(file: File, note: string): AxiosPromise<API_UploadFileResponse> {
		const formData = new FormData();
		formData.append('file', file);
		formData.append('note', note);
		const response = await this.client().post('/api/v1/generic/documento/upload', formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		});
		return response;
	}

	async sendFormRateizzazione(
		form: FormRateizzazioneType,
		files: Record<string, DocumentoAllegato>
	): AxiosPromise<API_SendFormRateizzazioneResponse> {
		// Devo avere un array di file per la rateizzazione
		//console.log('files', files, files);
		// cycle all the files saved in the files object

		form.filesAllegati = [] as { Id: string }[];

		for (const key of Object.keys(files)) {
			form.filesAllegati.push({ Id: files[key].Id });
		}
		//console.log('form', form);

		return await this.client().post('/api/v1/rateizzazione/new', form);
	}

	async sendFormProroga(
		form: FormProrogaType,
		files: Record<string, DocumentoAllegato>
	): AxiosPromise<API_SendFormProrogaResponse> {
		// Devo avere un array di file per la rateizzazione
		// //console.log('files', files, files);

		if (files && Object.keys(files).length > 0) {
			// cycle all the files saved in the files object

			form.filesAllegati = [] as { Id: string }[];

			for (const key of Object.keys(files)) {
				form.filesAllegati.push({ Id: files[key].Id });
			}

			// //console.log('form', form);

		}
		return await this.client().post('/api/v1/proroga/form/new', form);

		//  throw new Error('No files to upload');
	}

	// rimuove una posizione
	async deletePosizione(idPosizione: string): Promise<boolean> {
		try {
			const response = await this.client().delete(`/api/v1/posizione/delete/${idPosizione}`);

			return response.status === 200 || response.status === 201;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}

	//mezzi pubblicitari
	async sendFormNuovaDichiarazioneMezzoPermanente(
		form: NuovaDichiarazioneMezziPermanenti,
		files: Record<string, DocumentoAllegato>
	): Promise<boolean | AxiosPromise> {
		// Devo avere un array di file per la rateizzazione
		// //console.log('files', files, files);
		// if (files && Object.keys(files).length > 0) {
		// cycle all the files saved in the files object
		try {
			form.filesAllegati = [];

			for (const key of Object.keys(files)) {
				form.filesAllegati.push({
					Id: files[key].Id,
				});
			}
			// //console.log('form', form);
			const response = await this.client().post('/api/v1/mezzi_pubblicitari/new', form);
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
		// }

		// throw new Error('No files to upload');
	}
	async sendFormModificaDichiarazioneMezzoPermanente(
		form: DichiarazioneMezziPermanenti1,
		files: Record<string, DocumentoAllegato> = {}
	): Promise<boolean | AxiosPromise> {
		// Devo avere un array di file per la rateizzazione
		// //console.log('files', files, files);
		// if (files && Object.keys(files).length > 0) {
		// cycle all the files saved in the files object
		try {
			form.filesAllegati = [];

			for (const key of Object.keys(files)) {
				form.filesAllegati.push({
					Id: files[key].Id,
				});
			}
			console.debug('form', form);
			const response = await this.client().post(
				'/api/v1/mezzi_pubblicitari/dichiarazioni/' + form.Id,
				form
			);
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
		// }

		// throw new Error('No files to upload');
	}
	async sendModificaOCreaMezzoPermanente(
		mezzo: any, //TODO cambiare tipo
		files: Record<string, FileMP>
	): Promise<boolean | AxiosPromise> {
		// Devo avere un array di file per la rateizzazione
		// //console.log('files', files, files);
		// if (files && Object.keys(files).length > 0) {
		// cycle all the files saved in the files object
		try {
			mezzo.filesAllegati = [];

			for (const key of Object.keys(files)) {
				mezzo.filesAllegati.push({
					Id: files[key].Id,
					...(files[key].IsDeleted ? { IsDeleted: files[key].IsDeleted } : {}),
				});
			}

			const response = await this.client().post(
				'/api/v1/mezzi_pubblicitari/correlati/' +
				(mezzo.Tipologia_impianto_publicitario__c === TipiMezzi.Automezzo ? 'auto/' : 'altro/') +
				(mezzo.Id ? '' + mezzo.Id : ''),
				mezzo
			);
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
		// }

		// throw new Error('No files to upload');
	}
	async sendDeleteMezzoPermanente(id: string): Promise<boolean | AxiosPromise> {
		try {
			const response = await this.client().delete(
				'/api/v1/mezzi_pubblicitari/correlati/' + id + '/'
			);
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
		// }

		// throw new Error('No files to upload');
	}
	async sendCessaCorrelato(_body: any, mezzo: MezzoPermanente): Promise<boolean | AxiosPromise> {
		try {
			const response = await this.client().post(
				'/api/v1/mezzi_pubblicitari/correlati/cessazione/' +
				(mezzo.tipologia === TipiMezzi.Automezzo ? 'auto/' : 'altro/'),
				_body
			);
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
		// }

		// throw new Error('No files to upload');
	}

	async getDataPosizioneMezzoPermanente(idRappresentanza: string): Promise<boolean | AxiosPromise> {
		try {
			const response = await this.client().get(
				'/api/v1/mezzi_pubblicitari/posizione/' + idRappresentanza
			);
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
	}
	async getDichiarazione(id: string): Promise<boolean | AxiosPromise> {
		try {
			const response = await this.client().get('/api/v1/mezzi_pubblicitari/dichiarazioni/' + id);
			return response.data.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return false;
		}
		// }

		// throw new Error('No files to upload');
	}

	// Aggiunge nuovo documento da verificare (CRC - rateizza documento)
	async postVerificaDocumento(
		codice_utente: string,
		codice_controllo: string
	): Promise<boolean | List_DocumentiDaVerificare> {
		try {
			
			const response = await this.client().post('/api/v1/posizione/documents/check', {
				codice_utente,
				codice_controllo,
			});

			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return error;
		}
	}

	//PAGAMENTI

	//Check pagamento pagoPa
	async postCheckPagamentoPagoPa(formData: pagamentoPagoPa) {
		try {
			const response = await this.client().post('/api/v1/payments/pagopa/check', formData);

			return { success: true, data: response.data };
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return {
				success: false,
				error: error.response?.data || error.message,
				status: error.response?.status,
			};
		}
	}

	//Check ricevuta pagamento pagoPa
	async postCheckRicevutaPagamentoPagoPa(formData: pagamentoPagoPa) {
		try {
			const response = await this.client().post('/api/v1/payments/pagopa/ricevuta', formData);
			return { success: true, data: response.data };
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return {
				success: false,
				error: error.response?.data || error.message,
				status: error.response?.status,
			};
		}
	}

	//Check ricevuta pagamento pagoPa redirect
	async postCheckRicevutaPagamentoPagoPaRedirect(idSession: string) {
		// //console.log('idSession', idSession);

		try {
			const response = await this.client().get('/api/v1/payments/pagopa/ricevuta-redirect/' + idSession);
			return { success: true, data: response.data };
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return {
				success: false,
				error: error.response?.data || error.message,
				status: error.response?.status,
			};
		}
	}

	//Link pagamento pagoPa
	async postLinkPagamentoPagoPa(formData: pagamentoPagoPa) {
		try {
			const response = await this.client().post('/api/v1/payments/pagopa/richiesta', {
				IUV: formData.IUV,
				dominioEC: formData.dominioEC,
				emailDebitore: formData.emailDebitore,
			});

			return { success: true, data: response.data };
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return {
				success: false,
				error: error.response?.data || error.message,
				status: error.response?.status,
			};
		}
	}

	async getElencoEntiPagamento(): Promise<EnteGES[]> {
		try {
			const response = await this.client().get('/api/v1/payments/pagopa/elenco-enti/PPA');
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
		// }

		// throw new Error('No files to upload');
	}
	async getElencoEntiPagamentoPrecompilato(): Promise<EnteGES[]> {
		try {
			const response = await this.client().get('/api/v1/payments/pagopa/elenco-enti/LINK');
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
		// }

		// throw new Error('No files to upload');
	}

	async getElencoServiziPerEntePagamento(idEnte: number): Promise<ServizoPerEntePagamento[]> {
		try {
			const response = await this.client().get('/api/v1/payments/pagopa/elenco-servizi/PPA/' + idEnte);
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
		// }

		// throw new Error('No files to upload');
	}
	async getElencoServiziPerEntePagamentoPrecompilato(idEnte: number): Promise<ServizoPerEntePagamento[]> {
		try {
			const response = await this.client().get('/api/v1/payments/pagopa/elenco-servizi/LINK/' + idEnte);
			return response.data;
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return [];
		}
		// }

		// throw new Error('No files to upload');
	}

	async postPagamentoPagoPaNonPredeterminato(formData: pagamentoPagoPaNonPredeterminato) {
		try {
			const response = await this.client().post(
				'/api/v1/payments/pagopa/pagamento-non-predeterminato',
				formData
			);

			return { success: true, data: response.data };
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return {
				success: false,
				error: error.response?.data || error.message,
				status: error.response?.status,
			};
		}
	}

	async postRichiestaKeyMooney(formData: mooneyForm) {
		try {
			const response = await this.client().post('/api/v1/payments/mooney/richiesta', formData);

			return { success: true, data: response.data };
		} catch (error: any) {
			ErrorHandler.handleError(error);
			return {
				success: false,
				error: error.response?.data || error.message,
				status: error.response?.status,
			};
		}
	}
}

export default API;
